import { useParams, useLocation } from "wouter"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { BarChart3, TrendingUp, DollarSign, Calculator, ChevronDown, ChevronUp, ChevronLeft } from "lucide-react"
import { useValueAssessment } from "@/contexts/ValueAssessmentContext"
import { useProjectDetails } from "@/hooks/useProjectDetails"
import CostSavingsSummary from "@/components/CostSavingsSummary"
import { IconTile } from "@/components/IconTile"
import { useState, useEffect } from "react"
import { Slider } from "@/components/ui/slider"
import DiscountDebugger from "@/components/DiscountDebugger"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { TIME_CONFIG } from "@/../../shared/constants/timeConfig"
import { useDebouncedAutoSave } from '@/hooks/useDebouncedAutoSave'

// ─── Constants ──────────────────────────────────────────────────────────────
const SECTION_SPACING = "space-y-8"

/**
 * ValueAssessmentSummary
 * Displays calculated outputs of the Value Assessment process.
 * Two internal discount sliders (software + services) are unlockable via keyboard and persist to the blob.
 * All metrics are derived from formulas stored in the central calculation engine.
 * 
 * DATABASE TABLES USED:
 * - projects: Project metadata (client_org, name, value_assessment_pricing_enabled)
 * - value_assessment_results: Assessment data with summary_data JSONB blob
 * 
 * METRICS DISPLAYED:
 * - Deliverable Savings: From granularTotal calculation in CostSavingsSummary
 * - 5-Year Value: Deliverable savings × 5 (flat projection)
 * - ROI/NPV/Payback: From formulaEngine using formula_library table (pricing toggle)
 * 
 * DATA FLOW:
 * 1. useValueAssessment() fetches from /api/value-assessment/:projectId
 * 2. Data passed to CostSavingsSummary component
 * 3. CostSavingsSummary uses formulaEngine for calculations
 * 4. Results displayed in UI components
 * 
 * INTERNAL FEATURES:
 * - Discount sliders unlockable via Alt+Shift (Windows) or Option+Shift (Mac)
 * - Discounts persist to valueSummary.calculations blob
 * - All downstream calculations use discounted investment values
 */
export default function ValueAssessmentSummary() {
  // ─── URL Routing and Navigation ─────────────────────────────────────────── 
  const params = useParams()
  const [location, navigate] = useLocation()
  const projectId = params.projectId
  const queryClient = useQueryClient()
  
  // ─── Core Data Sources ─────────────────────────────────────────────────── 
  const { data, isLoading, valueSummary, updateSection } = useValueAssessment()
  const { data: project } = useProjectDetails(projectId)

  // ─── Mark Submission as Viewed ─────────────────────────────────────────── 
  const markViewedMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/mark-viewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      if (!response.ok) throw new Error('Failed to mark as viewed')
      return response.json()
    },
    onSuccess: () => {
      // Refresh project list to update the dashboard banner
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] })
    }
  })


  // ─── Mark submission as viewed when admin opens the page ─────────────── 
  useEffect(() => {
    if (valueSummary?.meta?.submissionStatus === 'submitted') {
      markViewedMutation.mutate()
    }
  }, [valueSummary?.meta?.submissionStatus])

  // ─── AUDIT STEP: Complete ValueSummary Blob Structure Logging ─────────────── 
  useEffect(() => {
    if (valueSummary) {

      // ADD HOURS SAVED CALCULATIONS IF MISSING - hours saved through automation
      if (valueSummary?.fteSavings?.fteCount && !valueSummary?.fteSavings?.hoursSaved) {
        // Get automation percentage from fteSavings or use reasonable default
        const automationPercent = valueSummary?.fteSavings?.automationPercent || valueSummary?.calculatedMetrics?.deliverableAutomation || 30
        const calculatedHours = Math.round(valueSummary.fteSavings.fteCount * (automationPercent / 100) * TIME_CONFIG.HOURS_PER_FTE_YEAR)

        // Update valueSummary with hoursSaved at top level
        const updatedValueSummary = {
          ...valueSummary,
          fteSavings: {
            ...valueSummary.fteSavings,
            hoursSaved: calculatedHours
          }
        }
        
        // Persist the update
        updateSection('summary_data', {
          valueSummary: updatedValueSummary
        })
      }
      
      // ADD PILLAR-LEVEL HOURS SAVED CALCULATIONS IF MISSING
      if (valueSummary?.pillarBreakdown) {
        let pillarUpdatesNeeded = false
        const updatedPillarBreakdown = { ...valueSummary.pillarBreakdown }
        
        Object.entries(valueSummary.pillarBreakdown).forEach(([pillar, pillarData]: [string, any]) => {
          if (pillarData?.fteCount && !pillarData?.hoursSaved) {
            // Calculate hours saved for this pillar with automation percentage
            const automationPercent = pillarData?.automationPercent || 30
            const calculatedPillarHours = Math.round(pillarData.fteCount * (automationPercent / 100) * TIME_CONFIG.HOURS_PER_FTE_YEAR)

            updatedPillarBreakdown[pillar] = {
              ...pillarData,
              hoursSaved: calculatedPillarHours
            }
            pillarUpdatesNeeded = true
          }
        })
        
        // Persist pillar updates if needed
        if (pillarUpdatesNeeded) {
          const updatedValueSummary = {
            ...valueSummary,
            pillarBreakdown: updatedPillarBreakdown
          }
          
          updateSection('summary_data', {
            valueSummary: updatedValueSummary
          })

        }
      }
      
      // FINAL CONFIRMATION: Log successful structure update
      if (valueSummary?.fteSavings?.hoursSaved && valueSummary?.pillarBreakdown) {
        const hasAllPillarHours = Object.entries(valueSummary.pillarBreakdown).every(([pillar, data]: [string, any]) => 
          data?.hoursSaved !== undefined
        )

      }
    }
  }, [valueSummary, updateSection])
  
  // ─── Internal Discount Sliders State ──────────────────────────────────────
  // Internal discount sliders (Option+Shift on Mac, Alt+Shift on Windows)
  // - discountSoftwarePercent: affects platform/licensing cost
  // - discountServicesPercent: affects services/implementation cost
  // Both values are stored in valueSummary.calculations
  // Adjusted costs feed into dynamic formula engine
  // All downstream metrics (ROI, Payback) use discounted total investment
  const [showDiscountSliders, setShowDiscountSliders] = useState(false)
  const [discountSoftwarePercent, setDiscountSoftwarePercent] = useState(0)
  const [discountServicesPercent, setDiscountServicesPercent] = useState(0)
  
  // ─── CRITICAL FIX: Restore discount values when data loads ─────────────────
  useEffect(() => {
    if (!projectId) return

    // First try to restore from localStorage (immediate persistence)
    const savedDiscounts = localStorage.getItem(`discounts_${projectId}`)
    if (savedDiscounts) {
      try {
        const parsed = JSON.parse(savedDiscounts)
        setDiscountSoftwarePercent(parsed.software || 0)
        setDiscountServicesPercent(parsed.services || 0)
        return
      } catch (error) {
        console.info('⚠️ Error parsing localStorage discounts:', error)
      }
    }
    
    // Fallback: Check database locations
    const sources = [
      { name: 'valueSummary.calculations', data: valueSummary?.calculations },
      { name: 'data.summary_data.valueSummary.calculations', data: data?.summary_data?.valueSummary?.calculations },
      { name: 'data.summary_data.calculations', data: data?.summary_data?.calculations }
    ]
    
    for (const source of sources) {
      if (source.data) {
        const savedSoftwareDiscount = source.data.discountSoftwarePercent
        const savedServicesDiscount = source.data.discountServicesPercent
        
        if (savedSoftwareDiscount !== undefined || savedServicesDiscount !== undefined) {
          setDiscountSoftwarePercent(savedSoftwareDiscount || 0)
          setDiscountServicesPercent(savedServicesDiscount || 0)
          break
        }
      }
    }
  }, [valueSummary, data, projectId])
  
  // ─── Simple toggle handler for discount sliders ───────────────────────────
  const toggleDiscountSliders = () => {

    setShowDiscountSliders(prev => !prev)
  }

  // ─── Setup debounced auto-save for discount values ─────────────────────────────
  const { debouncedSave: saveDiscounts } = useDebouncedAutoSave({
    debounceMs: 2000, // 2 second debounce for better performance
    endpoint: `/api/value-assessment/${projectId}`,
    onSuccess: () => {
    },
    onError: (error) => {
      console.error('❌ Error saving discount values:', error);
    }
  });

  // ─── Persist Discount Values (Dual: localStorage + Database) ──────────────── 
  useEffect(() => {
    if (!projectId) return
  
    // Immediately save to localStorage for instant persistence
    const discountData = {
      software: discountSoftwarePercent,
      services: discountServicesPercent,
      timestamp: new Date().toISOString()
    }
  
    localStorage.setItem(`discounts_${projectId}`, JSON.stringify(discountData))

    // Also update database (with proper debouncing)
    if (!updateSection) return
  
    const currentSoftwareDiscount = valueSummary?.calculations?.discountSoftwarePercent || 0
    const currentServicesDiscount = valueSummary?.calculations?.discountServicesPercent || 0
  
    if (discountSoftwarePercent !== currentSoftwareDiscount || 
        discountServicesPercent !== currentServicesDiscount) {
    
      const updatedCalculations = {
        ...(valueSummary?.calculations || {}),
        discountSoftwarePercent: discountSoftwarePercent,
        discountServicesPercent: discountServicesPercent
      }
    
      // Preserve current state and update database
      const currentPricingEnabled = data?.summary_data?.valueSummary?.pricingEnabled ?? valueSummary?.pricingEnabled ?? false
    
      // Use the debounced save function instead of setTimeout
      const payload = {
        data: {
          summary_data: {
            ...(data?.summary_data || {}),
            valueSummary: {
              ...(data?.summary_data?.valueSummary || {}),
              pricingEnabled: currentPricingEnabled,
              calculations: updatedCalculations
            }
          }
        }
      };
    
      // This will automatically debounce the API call
      saveDiscounts(payload);
    }
  }, [discountSoftwarePercent, discountServicesPercent, projectId, updateSection, valueSummary, data, saveDiscounts])
  
  // Data structure validation - removed startup audit logs per cleanup requirements
  
  // ─── Pricing State Logic ─────────────────────────────────────────────────
  // Prefer project-level flag from EditProjectModal, then fall back to value assessment blob
  const pricingEnabled = (project as any)?.show_financial_metrics
    ?? (project as any)?.value_assessment_pricing_enabled
    ?? data?.summary_data?.valueSummary?.pricingEnabled
    ?? valueSummary?.pricingEnabled
    ?? false
  
  // ─── Navigation Handlers ─────────────────────────────────────────────────── 
  const handleReturnToDashboard = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    // Navigate to the previous step in the value assessment flow
    navigate(`/value-assessment/${projectId}/configuration`);
  };

  // ─── Pricing Toggle Handler ─────────────────────────────────────────────── 
  // Updates pricingEnabled flag in valueSummary blob without page reload
  const handlePricingToggle = async (enabled: boolean) => {
    if (!projectId || !updateSection) return;

    try {
      // CRITICAL FIX: Fetch fresh data first to avoid race conditions
      const freshResponse = await fetch(`/api/value-assessment/${projectId}`);
      if (!freshResponse.ok) throw new Error('Failed to fetch fresh data');
      
      const freshData = await freshResponse.json();

      const payload = {
        summary_data: {
          ...freshData.summary_data, // Use FRESH data instead of potentially stale data
          valueSummary: {
            ...freshData.summary_data?.valueSummary,
            pricingEnabled: enabled
          }
        }
      };

      const response = await fetch(`/api/value-assessment/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: payload }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to toggle pricing: ${response.status}`);
      }

      // Update context state to trigger re-render without page reload
      await updateSection('summary_data', payload.summary_data);
    } catch (error) {
      console.error("❌ Pricing Toggle Error:", error);
    }
  };

  // ─── Loading State ─────────────────────────────────────────────────────── 
  // Displays centered spinner while data loads with consistent branding
  if (isLoading) {
    return (
      <div className=" text-foreground min-h-screen p-6">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground">Loading assessment data...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ─── Main Render ───────────────────────────────────────────────────────── 
  return (
    <div className="value-assessment-summary-container min-h-screen p-6 bg-background text-foreground">
      <div className="w-full max-w-7xl mx-auto space-y-8">
        
        {/* ─── Header Block ──────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-border">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <IconTile icon={BarChart3} color="primary" bg="background" size={24} iconSize={24}/>
              <h1 className="text-3xl font-bold text-foreground">Value Assessment Summary</h1>
            </div>
            <p className="text-base text-muted-foreground">
              {project?.client_org && project?.name
                  ? `${project.client_org} - ${project.name}`
                  : project?.name || 'Comprehensive assessment overview'}
            </p>
          </div>
        </div>



        {/* ─── Strategic Summary Block ────────────────────────────────────── */}
        {/* Main cost savings analysis component with all charts, tables, and metrics */}
        <div id="strategic-value-summary" className={SECTION_SPACING}>
          {projectId && (
            <>
              <DiscountDebugger 
                discountSoftwarePercent={discountSoftwarePercent}
                discountServicesPercent={discountServicesPercent}
              />
              <CostSavingsSummary 
                projectId={projectId} 
                isPublicView={false}
                data={data}
                showFinancials={pricingEnabled}
                // Export Analysis visibility should follow the project's export_options_enabled
                // and be independent from pricingEnabled
                showExportOptions={(project as any)?.export_options_enabled ?? true}
                discountSoftwarePercent={discountSoftwarePercent}
                discountServicesPercent={discountServicesPercent}
                showDiscountSliders={showDiscountSliders}
                onToggleDiscountSliders={toggleDiscountSliders}
                onDiscountSoftwareChange={setDiscountSoftwarePercent}
                onDiscountServicesChange={setDiscountServicesPercent}
                projectYearlyCosts={(project as any)?.yearly_costs || undefined}
              />
            </>
          )}
        </div>

        {/* ─── Navigation Actions Block ───────────────────────────────────── */}
        {/* Bottom navigation with back and dashboard buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleGoBack}
            className="flex items-center space-x-2"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={handleReturnToDashboard}
            className="flex items-center space-x-2"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Return to Dashboard</span>
          </Button>
        </div>

      </div>
    </div>
  )
}
